/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, FormControlLabel, FormGroup,
  IconButton, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HistoryIcon from '@mui/icons-material/History'
import SendIcon from '@mui/icons-material/SendOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { webhooksAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabel, formatDate, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import ConfirmDialog from '../common/ConfirmDialog'
import EmptyState from '../common/EmptyState'
import CopyField from './CopyField'

const EVENTS = [
  { value: 'record.created', label: 'A record is created' },
  { value: 'record.updated', label: 'A record is updated' },
  { value: 'record.deleted', label: 'A record is deleted' },
]
const STATUS_COLOR = { SENT: 'success', FAILED: 'error', PENDING: 'warning', SENDING: 'info' }

const WebhookDialog = ({ webhook, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const { entityTypes } = useEntityTypes()
  const [v, setV] = useState(() => webhook || { name: '', url: 'https://', events: ['record.created'], entityTypes: [], enabled: true })
  const set = (patch) => setV((x) => ({ ...x, ...patch }))
  const save = useMutation({
    mutationFn: () => (webhook ? webhooksAPI.update(webhook.id, v) : webhooksAPI.create(v)),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'webhooks'] }); onClose() },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{webhook ? 'Edit webhook' : 'New webhook'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          <TextField label="Name" required value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Google Sheet sync" inputProps={{ maxLength: 100 }} />
          <TextField label="Send to (URL)" required value={v.url} onChange={(e) => set({ url: e.target.value })} helperText="https only; it gets a signed JSON POST for each change" />
          <FormGroup>
            {EVENTS.map((e) => (
              <FormControlLabel key={e.value} label={e.label} control={<Checkbox checked={v.events.includes(e.value)}
                onChange={(ev) => set({ events: ev.target.checked ? [...v.events, e.value] : v.events.filter((x) => x !== e.value) })} />} />
            ))}
          </FormGroup>
          <Autocomplete multiple options={entityTypes} value={v.entityTypes} getOptionLabel={entityLabel} onChange={(_, list) => set({ entityTypes: list })}
            renderInput={(p) => <TextField {...p} label="Record types" placeholder={v.entityTypes.length ? '' : 'All record types'} />} />
          <FormControlLabel control={<Switch checked={v.enabled} onChange={(e) => set({ enabled: e.target.checked })} />} label="On" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !v.name.trim() || !v.events.length} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

const Deliveries = ({ webhook, onClose }) => {
  const { data = [], isLoading } = useQuery({ queryKey: ['webhook-deliveries', webhook.id], queryFn: () => webhooksAPI.deliveries(webhook.id), refetchInterval: 5000 })
  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 2 } }}>
      <Typography variant="h6">{webhook.name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Last 50 deliveries · failed ones are retried for about 15 hours</Typography>
      {isLoading && <Typography variant="body2">Loading…</Typography>}
      {!isLoading && data.length === 0 && <Typography variant="body2" color="text.secondary">Nothing sent yet.</Typography>}
      <Stack spacing={1}>
        {data.map((d) => (
          <Paper key={d.id} variant="outlined" sx={{ p: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color={STATUS_COLOR[d.status]} label={d.status.toLowerCase()} variant="outlined" />
              <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>{d.event}</Typography>
              <Tooltip title={formatDate(d.createdOn)}><Typography variant="caption" color="text.secondary">{formatRelativeTime(d.createdOn)}</Typography></Tooltip>
            </Stack>
            {(d.lastError || d.lastStatusCode) && (
              <Typography variant="caption" color={d.status === 'SENT' ? 'text.secondary' : 'error'} sx={{ display: 'block', mt: 0.5 }}>
                {d.lastStatusCode ? `HTTP ${d.lastStatusCode}` : ''}{d.lastError ? ` ${d.lastError}` : ''}
                {d.status === 'PENDING' && d.attempts > 0 ? ` · try ${d.attempts + 1} ${formatRelativeTime(d.nextAttemptAt)}` : ''}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </Drawer>
  )
}

const WebhooksTab = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [viewing, setViewing] = useState(null)
  const { data: hooks = [] } = useQuery({ queryKey: [tenantId, 'webhooks'], queryFn: webhooksAPI.list })
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'webhooks'] })
  const test = useMutation({ mutationFn: webhooksAPI.test, onSuccess: () => { notify('Test sent; see the deliveries'); refresh() }, onError: (e) => notify(getErrorMessage(e), 'error') })
  const remove = useMutation({ mutationFn: webhooksAPI.remove, onSuccess: () => { setRemoving(null); refresh() } })
  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Tell another system the moment a record changes. Each delivery is signed with the webhook’s secret (X-Credintel-Signature).
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 2 }} onClick={() => setEditing('new')}>New webhook</Button>
      </Stack>
      {hooks.length === 0 && <Paper variant="outlined"><EmptyState title="No webhooks" description="Connect a Google Sheet, Slack via Zapier, or your own system." /></Paper>}
      <Stack spacing={2}>
        {hooks.map((h) => (
          <Paper key={h.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>{h.name} {!h.enabled && <Chip size="small" label="off" sx={{ ml: 1 }} />}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{h.url}</Typography>
              </Box>
              <Tooltip title="Send a test"><IconButton onClick={() => test.mutate(h.id)} aria-label={`Test ${h.name}`}><SendIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Deliveries"><IconButton onClick={() => setViewing(h)} aria-label={`Deliveries of ${h.name}`}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Edit"><IconButton onClick={() => setEditing(h)} aria-label={`Edit ${h.name}`}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton onClick={() => setRemoving(h)} aria-label={`Delete ${h.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 1 }}>
              {h.events.join(', ')} · {h.entityTypes.length ? h.entityTypes.map(entityLabel).join(', ') : 'all record types'} ·
              {' '}{h.stats.sent} sent, {h.stats.failed} failed{h.stats.pending ? `, ${h.stats.pending} waiting` : ''}
            </Typography>
            <CopyField label="Signing secret" value={h.secret} helperText="Check each delivery: HMAC-SHA256 of “<t>.<body>” with this secret equals v1 in X-Credintel-Signature." />
          </Paper>
        ))}
      </Stack>
      {editing && <WebhookDialog webhook={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {viewing && <Deliveries webhook={viewing} onClose={() => setViewing(null)} />}
      <ConfirmDialog open={!!removing} title="Delete webhook?" message={`“${removing?.name}” stops receiving changes; waiting deliveries are dropped.`}
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate(removing.id)} onClose={() => setRemoving(null)} />
    </Box>
  )
}

export default WebhooksTab
